import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Bed, Bath, Home, Maximize, ChevronDown, Info } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import SearchableSelect from "@/components/SearchableSelect";
import PersonalizedSearchEnquiry from "@/components/PersonalizedSearchEnquiry";
import PropertyStatusRibbon from "@/components/PropertyStatusRibbon";
import SEO from "@/components/SEO";
import { API } from "@/config/api";
import { buildPropertyQueryParams, readPropertySearchParams } from "@/utils/propertySearchParams";
import { resolveMediaUrl } from "@/utils/resolveMediaUrl";

// Format price with Swiss formatting
const formatPrice = (price) => {
  return new Intl.NumberFormat('de-CH', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price).replace(/'/g, "'");
};

// Property Card Component
const PropertyCard = ({ property, index, t }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const images = property.images || [];

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance && images.length > 1) {
      if (diff > 0) {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      } else {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleMobileInfoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileInfo(!showMobileInfo);
  };

  const showOverlay = isHovered || showMobileInfo;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Link 
        to={`/properties/${property.id}`}
        className="block group"
        data-testid={`property-card-${property.id}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div 
          className="relative aspect-[4/3] overflow-hidden media-frame"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={resolveMediaUrl(images[currentImageIndex] || images[0])}
            alt={property.title}
            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
          />

          <PropertyStatusRibbon status={property.status} />

          {/* Mobile info button */}
          <button
            onClick={handleMobileInfoClick}
            className="md:hidden absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm"
            aria-label="Show property info"
          >
            <Info size={16} className="text-[#1A1A1A]" strokeWidth={1.5} />
          </button>
          
          {/* Price overlay - hidden on hover/info */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-12 pb-5 px-5 pointer-events-none z-[5] transition-opacity duration-300 ${
            showOverlay ? 'opacity-0' : 'opacity-100'
          }`}>
            <p className="price-display font-normal inline-block text-white drop-shadow-lg bg-black/20 backdrop-blur-sm px-3 py-1 rounded-sm">
              CHF {formatPrice(property.price)}.-
            </p>
          </div>
          
          {/* Hover/Info overlay - Soft neutral tone with better layout */}
          <div className={`absolute inset-0 bg-[#1A1A1A]/85 transition-all duration-500 flex flex-col justify-end p-6 ${
            showOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            {/* Close button for mobile */}
            {showMobileInfo && (
              <button
                onClick={handleMobileInfoClick}
                className="md:hidden absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                aria-label="Close info"
              >
                <span className="text-white text-lg">×</span>
              </button>
            )}
            
            {/* Description section */}
            <div className="mb-4">
              <p className="text-white/70 text-[10px] uppercase tracking-[0.15em] mb-2">
                {t('propertyCard.description')}
              </p>
              <p className="text-white font-light text-sm leading-relaxed line-clamp-3">
                {property.short_description}
              </p>
            </div>
            
            {/* Price */}
            <div className="mb-4 pb-4 border-b border-white/20">
              <p className="price-display font-normal text-white">
                CHF {formatPrice(property.price)}.-
              </p>
            </div>
            
            {/* Bottom section - Property metrics with icons */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-center gap-1">
                <Bed size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.bedrooms}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Bath size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.bathrooms}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Maximize size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.size_sqm} m²</span>
              </div>
              {property.land_sqm > 0 && (
                <div className="flex flex-col items-center gap-1">
                  <Home size={18} className="text-white" strokeWidth={1.5} />
                  <span className="text-white text-xs font-light">{property.land_sqm} m²</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Image navigation - Desktop only */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.preventDefault(); setCurrentImageIndex((prev) => prev === 0 ? images.length - 1 : prev - 1); }}
                className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronLeft size={16} className="text-[#1A1A1A]" />
              </button>
              <button
                onClick={(e) => { e.preventDefault(); setCurrentImageIndex((prev) => prev === images.length - 1 ? 0 : prev + 1); }}
                className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronRight size={16} className="text-[#1A1A1A]" />
              </button>
              {/* Image dots - always visible on mobile, hover on desktop */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                {images.map((_, idx) => (
                  <span key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/40'}`} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Property info */}
        <div className="pt-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1A1A1A] mb-2">
            {property.location}
          </p>
          <h3 className="heading-secondary mb-2 group-hover:text-[#8B7355] transition-colors">
            {property.title}
          </h3>
          <p className="text-sm text-[#555] font-normal tracking-wide">
            {property.rooms && `${property.rooms} pièces · `}{property.size_sqm} m²{property.bedrooms && ` · ${property.bedrooms} ch.`}
          </p>
        </div>
      </Link>
    </motion.div>
  );
};

const AcheterPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialFilters = readPropertySearchParams(searchParams);
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedLocation, setSelectedLocation] = useState(initialFilters.location);
  const [selectedBudget, setSelectedBudget] = useState("");
  const [selectedBedrooms, setSelectedBedrooms] = useState(initialFilters.minBedrooms);
  const [selectedPropertyType, setSelectedPropertyType] = useState(initialFilters.propertyType);
  const [selectedMinSurface, setSelectedMinSurface] = useState(initialFilters.minSurface);
  const [maxPrice, setMaxPrice] = useState(initialFilters.maxPrice);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState("");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  const sortOptions = [
    { value: "", label: language === 'fr' ? 'Trier par' : 'Sort by' },
    { value: "price_asc", label: language === 'fr' ? 'Prix croissant' : 'Price: Low to High' },
    { value: "price_desc", label: language === 'fr' ? 'Prix décroissant' : 'Price: High to Low' },
    { value: "size_asc", label: language === 'fr' ? 'Surface croissante' : 'Size: Small to Large' },
    { value: "size_desc", label: language === 'fr' ? 'Surface décroissante' : 'Size: Large to Small' },
    { value: "newest", label: language === 'fr' ? 'Plus récents' : 'Newest' }
  ];
  
  // Sort properties
  const sortedProperties = useMemo(() => {
    if (!sortBy) return properties;
    
    return [...properties].sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        case 'size_asc':
          return a.size_sqm - b.size_sqm;
        case 'size_desc':
          return b.size_sqm - a.size_sqm;
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });
  }, [properties, sortBy]);

  // Budget options for purchase
  const budgetOptions = [
    { value: "", label: language === 'fr' ? 'Budget' : 'Budget' },
    { value: "0-5000000", label: "< CHF 5M" },
    { value: "5000000-10000000", label: "CHF 5M - 10M" },
    { value: "10000000-20000000", label: "CHF 10M - 20M" },
    { value: "20000000-50000000", label: "CHF 20M - 50M" },
    { value: "50000000-999999999", label: "> CHF 50M" }
  ];

  const bedroomOptions = [
    { value: "", label: language === 'fr' ? 'Pièces' : 'Rooms' },
    { value: "1", label: "1+" },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
    { value: "5", label: "5+" },
    { value: "6", label: "6+" }
  ];

  const propertyTypeOptions = [
    { value: "", label: language === 'fr' ? 'Type de bien' : 'Property Type' },
    ...propertyTypes.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
    })),
  ];

  const surfaceOptions = [
    { value: "", label: language === 'fr' ? 'Surface min.' : 'Min. surface' },
    { value: "50", label: "> 50 m²" },
    { value: "100", label: "> 100 m²" },
    { value: "150", label: "> 150 m²" },
    { value: "200", label: "> 200 m²" },
    { value: "300", label: "> 300 m²" },
    { value: "500", label: "> 500 m²" }
  ];

  const handleFilterSearch = () => {
    // Listing filters update live via useEffect; enquiry form below captures leads.
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = buildPropertyQueryParams({
          listingType: 'sale',
          location: selectedLocation,
          maxPrice,
          minSurface: selectedMinSurface,
          minBedrooms: selectedBedrooms,
          propertyType: selectedPropertyType,
          budgetRange: selectedBudget,
        });

        const [propsRes, locsRes, typesRes] = await Promise.all([
          axios.get(`${API}/properties?${params.toString()}`),
          axios.get(`${API}/locations`),
          axios.get(`${API}/property-types`),
        ]);
        
        setProperties(propsRes.data);
        setPropertyTypes(typesRes.data);
        setLocations([
          { value: "", label: language === 'fr' ? 'Localisation' : 'Location' },
          ...locsRes.data.map(loc => ({ value: loc, label: loc }))
        ]);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLocation, selectedBudget, selectedBedrooms, selectedPropertyType, selectedMinSurface, maxPrice, language]);

  return (
    <main className="min-h-screen bg-byron-bg" data-testid="acheter-page">
      <SEO 
        title="Acheter"
        description="Découvrez notre sélection de propriétés de prestige à vendre à Genève. Villas, appartements et penthouses de luxe à Cologny, Vandoeuvres, Collonge-Bellerive."
        canonical="/acheter"
      />
      {/* Hero Section - Wide framed video with readable text overlay */}
      <section className="relative bg-byron-bg">
        <div className="header-spacer bg-byron-bg" />
        
        <div className="media-frame-inset">
          <div className="relative h-[55vh] min-h-[420px] md:h-[68vh] overflow-hidden">
            <video
              src="/Infinity_Pool_Byron.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Stronger scrim for white text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/20" />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute bottom-0 left-0 right-0 p-6 md:p-10"
            >
              <div className="max-w-xl">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white mb-3 drop-shadow-sm">
                  {language === 'fr' ? 'Accès privilégié' : 'Private Access'}
                </p>
                <h1 className="font-heading text-3xl md:text-4xl font-light text-white mb-4 tracking-wide drop-shadow-sm">
                  {language === 'fr' ? 'Off Market' : 'Off Market'}
                </h1>
                <p className="text-white text-sm md:text-base font-light leading-relaxed max-w-lg mb-6 drop-shadow-sm">
                  {language === 'fr' 
                    ? 'Accédez à des opportunités exclusives, confidentielles et non diffusées publiquement. Notre réseau privilégié vous ouvre les portes de biens exceptionnels avant leur mise sur le marché.'
                    : 'Access exclusive, confidential opportunities not publicly listed. Our privileged network opens doors to exceptional properties before they reach the market.'}
                </p>
                <Link
                  to="/off-market"
                  className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.15em] hover:text-white/90 transition-colors group"
                  data-testid="off-market-cta"
                >
                  {language === 'fr' ? 'Demander l\'accès' : 'Request access'}
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Filter Bar - Full detailed filter */}
      <section className="py-5 bg-byron-bg">
        <div className="container-byron">
          <div className="bg-black/80 border border-white/10">
            {/* Main Filter Row */}
            <div className="flex flex-wrap lg:flex-nowrap">
              {/* Location */}
              <div className="w-full sm:w-1/2 lg:flex-1 p-4 border-b sm:border-b lg:border-b-0 lg:border-r border-white/15">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">
                  {language === 'fr' ? 'Localisation' : 'Location'}
                </label>
                <SearchableSelect
                  options={locations}
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  placeholder={language === 'fr' ? 'Toutes les régions' : 'All regions'}
                  variant="glass"
                  inputClassName="text-white placeholder:text-white/60 bg-transparent"
                />
              </div>
              
              {/* Budget */}
              <div className="w-1/2 lg:flex-1 p-4 border-b lg:border-b-0 lg:border-r border-white/15">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">
                  {language === 'fr' ? 'Budget' : 'Budget'}
                </label>
                <SearchableSelect
                  options={budgetOptions}
                  value={selectedBudget}
                  onChange={setSelectedBudget}
                  placeholder={language === 'fr' ? 'Tous budgets' : 'All budgets'}
                  variant="glass"
                  inputClassName="text-white placeholder:text-white/60 bg-transparent"
                />
              </div>
              
              {/* Pièces */}
              <div className="w-1/2 lg:flex-1 p-4 border-b lg:border-b-0 lg:border-r border-white/15">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">
                  {language === 'fr' ? 'Pièces' : 'Rooms'}
                </label>
                <SearchableSelect
                  options={bedroomOptions}
                  value={selectedBedrooms}
                  onChange={setSelectedBedrooms}
                  placeholder={language === 'fr' ? 'Toutes pièces' : 'Any'}
                  variant="glass"
                  inputClassName="text-white placeholder:text-white/60 bg-transparent"
                />
              </div>
              
              {/* Property Type */}
              <div className="w-1/2 lg:flex-1 p-4 border-b lg:border-b-0 lg:border-r border-white/15">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">
                  {language === 'fr' ? 'Type de bien' : 'Property Type'}
                </label>
                <SearchableSelect
                  options={propertyTypeOptions}
                  value={selectedPropertyType}
                  onChange={setSelectedPropertyType}
                  placeholder={language === 'fr' ? 'Tous types' : 'All types'}
                  variant="glass"
                  inputClassName="text-white placeholder:text-white/60 bg-transparent"
                />
              </div>
              
              {/* Surface Min */}
              <div className="w-1/2 lg:flex-1 p-4 border-b lg:border-b-0 lg:border-r border-white/15">
                <label className="block text-[10px] uppercase tracking-[0.15em] text-white/50 mb-2">
                  {language === 'fr' ? 'Surface min.' : 'Min. surface'}
                </label>
                <SearchableSelect
                  options={surfaceOptions}
                  value={selectedMinSurface}
                  onChange={setSelectedMinSurface}
                  placeholder={language === 'fr' ? 'Toutes surfaces' : 'Any'}
                  variant="glass"
                  inputClassName="text-white placeholder:text-white/60 bg-transparent"
                />
              </div>
              
              {/* More Options + Search */}
              <div className="w-full lg:w-auto p-4 flex items-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowMoreFilters(!showMoreFilters)}
                  className="px-4 py-3 border border-white/25 hover:border-white/50 text-white/70 hover:text-white text-[11px] uppercase tracking-[0.15em] transition-colors flex items-center gap-2"
                  data-testid="more-filters-button"
                >
                  {language === 'fr' ? 'Plus' : 'More'}
                  <ChevronDown size={14} className={`transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleFilterSearch}
                  className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white text-[11px] uppercase tracking-[0.15em] transition-colors"
                  data-testid="search-button"
                >
                  {language === 'fr' ? 'Rechercher' : 'Search'}
                </button>
              </div>
            </div>
            
            {/* Expanded More Filters */}
            {showMoreFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-white/15 mt-1"
              >
                <div className="flex flex-wrap p-4 gap-6">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="pool" 
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="pool" className="text-sm text-white/80">
                      {language === 'fr' ? 'Piscine' : 'Pool'}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="garden" 
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="garden" className="text-sm text-white/80">
                      {language === 'fr' ? 'Jardin' : 'Garden'}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="parking" 
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="parking" className="text-sm text-white/80">
                      {language === 'fr' ? 'Parking' : 'Parking'}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="lakeView" 
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="lakeView" className="text-sm text-white/80">
                      {language === 'fr' ? 'Vue lac' : 'Lake view'}
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="newBuild" 
                      className="w-4 h-4 accent-white"
                    />
                    <label htmlFor="newBuild" className="text-sm text-white/80">
                      {language === 'fr' ? 'Neuf' : 'New build'}
                    </label>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-10 md:py-14 bg-byron-bg">
        <div className="container-byron">
          <div className="flex items-center justify-between mb-12">
            <p className="text-sm text-[#555] font-normal tracking-wide">
              {properties.length} {language === 'fr' ? 'biens à vendre' : 'properties for sale'}
            </p>
            
            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 text-sm text-[#555] hover:text-[#1A1A1A] transition-colors"
                data-testid="sort-button"
              >
                <span>{sortOptions.find(opt => opt.value === sortBy)?.label || (language === 'fr' ? 'Trier par' : 'Sort by')}</span>
                <ChevronDown size={16} className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#E5E5E0] shadow-lg z-20 min-w-[180px]">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`block w-full text-left px-4 py-3 text-sm transition-colors ${
                        sortBy === option.value 
                          ? 'text-[#1A1A1A] bg-[#F8F7F5]' 
                          : 'text-[#555] hover:bg-[#F8F7F5] hover:text-[#1A1A1A]'
                      }`}
                      data-testid={`sort-option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#8B7355] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : properties.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#555] font-normal">
                {language === 'fr' ? 'Aucun bien trouvé' : 'No properties found'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {sortedProperties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} t={t} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Personalized search enquiry — lead capture */}
      <PersonalizedSearchEnquiry
        searchType="acheter"
        sourcePage="/acheter"
        searchMode="buy"
        title={language === "fr" ? "Recherchez votre bien" : "Find your property"}
        intro={
          language === "fr"
            ? "Partagez vos critères et vos coordonnées. Notre équipe vous proposera une sélection personnalisée de biens à vendre."
            : "Share your criteria and contact details. Our team will propose a tailored selection of properties for sale."
        }
        helperText=""
        continueLabel={language === "fr" ? "Continuer" : "Continue"}
        addressPlaceholder={
          language === "fr"
            ? "Ville, région ou code postal..."
            : "City, region or zip code..."
        }
        testIdPrefix="acheter-search"
      />
    </main>
  );
};

export default AcheterPage;
